import { useState, useCallback, useRef } from 'react';
import { isGrpcRuntimeAvailable } from '../../../shared/api/grpc/transport';
import {
  getQuickFillDataGrpc,
  type QuickFillDataScope,
  type QuickFillSnapshotShapeOption,
} from '../../../shared/api/grpc/quickFillClient';
import type { RevitOperationProgressSnapshot } from '../../../shared/api/grpc/operationProgressClient';
import { perf } from '../../../shared/lib/perfLogger';
import type { RevitFamily } from '../../../shared/api/revit/types';
import {
  appendRequestedParameterIntentCacheIdentity,
  readCacheScopeModelVersion,
  replaceCacheScopeModelVersion,
  type RequestedParameterIntentInput,
} from '../../../shared/api/grpc/requestedParameterIntent';
import {
  type QuickFillPipeElement,
  type QuickFillDataSnapshot,
  normalizeSnapshotShape,
  normalizeCacheScopeKey,
  shouldUseQuickFillBrowserCache,
  buildDataScopeKey,
  buildQuickFillDataCacheKey,
  findCompatibleCacheKey,
  quickFillResponseCache,
  quickFillInFlightRequests,
  readQuickFillSessionSnapshot,
  readQuickFillPersistentSnapshot,
  readQuickFillPersistentMarkers,
  hasQuickFillPersistentMarker,
  writeQuickFillSessionSnapshot,
  writeQuickFillPersistentSnapshot,
  scopeQuickFillSnapshot,
  getLastQuickFillDataCacheProbeDetails,
  hasQuickFillDataCache,
  __resetQuickFillDataCacheForTests,
  __resetQuickFillDataMemoryCacheForTests,
} from '../lib/quickFillDataCache';
import {
  logQuickFillSnapshotSuccess,
  mapQuickFillGrpcResultToSnapshot,
} from '../lib/quickFillSnapshotMapper';

export interface AvailableParam {
  name: string;
  parameterType: string;
  group: string;
  isInstance: boolean;
}

interface QuickFillDataState {
  families: RevitFamily[];
  pipeElements: QuickFillPipeElement[];
  availableParameters: AvailableParam[];
  isLoading: boolean;
  isLoaded: boolean;
  error: string | null;
  snapshotShape: QuickFillSnapshotShapeOption | null;
  cacheScopeKey: string | null;
  dataScopeKey: string;
}

export interface QuickFillFetchResult {
  success: boolean;
  source: 'network' | 'cache' | 'joined' | 'fallback' | 'unavailable' | 'error';
  familiesCount: number;
  elementsCount: number;
  availableParametersCount: number;
  modelVersion?: number;
  cacheScopeKey?: string;
}

export interface QuickFillFetchOptions {
  onProgress?: (progress: RevitOperationProgressSnapshot) => void;
  snapshotShape?: QuickFillSnapshotShapeOption;
  cacheScopeKey?: string | null;
  dataScope?: QuickFillDataScope;
  deferUnplacedFamilyCatalog?: boolean;
  requestedParameters?: readonly RequestedParameterIntentInput[];
}

const buildSuccessState = (
  snapshot: QuickFillDataSnapshot,
  snapshotShape: QuickFillSnapshotShapeOption,
  cacheScopeKey: string,
  dataScopeKey: string
): QuickFillDataState => ({
  families: snapshot.families,
  pipeElements: snapshot.pipeElements,
  availableParameters: snapshot.availableParameters,
  isLoading: false,
  isLoaded: true,
  error: null,
  snapshotShape,
  cacheScopeKey,
  dataScopeKey,
});

const buildLoadingState = (
  previous: QuickFillDataState,
  snapshotShape: QuickFillSnapshotShapeOption,
  cacheScopeKey: string,
  dataScopeKey: string
): QuickFillDataState => {
  if (
    previous.isLoaded &&
    previous.snapshotShape === snapshotShape &&
    previous.cacheScopeKey === cacheScopeKey &&
    previous.dataScopeKey === dataScopeKey
  ) {
    return {
      ...previous,
      isLoading: true,
      error: null,
    };
  }

  return {
    families: [],
    pipeElements: [],
    availableParameters: [],
    isLoading: true,
    isLoaded: false,
    error: null,
    snapshotShape,
    cacheScopeKey,
    dataScopeKey,
  };
};

const buildErrorState = (message: string): Partial<QuickFillDataState> => ({
  error: message,
  isLoading: false,
  isLoaded: true,
});

const buildFetchFailureResult = (
  source: QuickFillFetchResult['source']
): QuickFillFetchResult => ({
  success: false,
  source,
  familiesCount: 0,
  elementsCount: 0,
  availableParametersCount: 0,
});

export const __quickFillDataTestInternals = {
  scopeQuickFillSnapshot,
};

export async function fetchQuickFillSnapshot(
  parameterNames?: string[],
  options?: Pick<QuickFillFetchOptions, 'onProgress' | 'snapshotShape' | 'dataScope' | 'cacheScopeKey' | 'deferUnplacedFamilyCatalog' | 'requestedParameters'>
): Promise<QuickFillDataSnapshot> {
  if (!isGrpcRuntimeAvailable()) {
    throw new Error('Quick Fill data is not available outside the Revit gRPC runtime.');
  }

  const snapshotShape = normalizeSnapshotShape(options?.snapshotShape);
  const result = await getQuickFillDataGrpc(parameterNames, {
    onProgress: options?.onProgress,
    snapshotShape,
    dataScope: options?.dataScope,
    deferUnplacedFamilyCatalog: options?.deferUnplacedFamilyCatalog,
    requestedParameters: options?.requestedParameters,
  });

  return mapQuickFillGrpcResultToSnapshot(result, options?.dataScope, parameterNames);
}

const INITIAL_STATE: QuickFillDataState = {
  families: [],
  pipeElements: [],
  availableParameters: [],
  isLoading: false,
  isLoaded: false,
  error: null,
  snapshotShape: null,
  cacheScopeKey: null,
  dataScopeKey: 'scope:all',
};

/** Loads the scoped Quick Fill snapshot, preferring the versioned browser cache. */
export function useQuickFillData() {
  const [state, setState] = useState<QuickFillDataState>(INITIAL_STATE);
  const latestRequestIdRef = useRef(0);

  const fetchQuickFillData = useCallback(async (
    parameterNames?: string[],
    options?: QuickFillFetchOptions
  ): Promise<QuickFillFetchResult> => {
    const requestId = ++latestRequestIdRef.current;
    const setLatestState = (
      update: QuickFillDataState | ((previous: QuickFillDataState) => QuickFillDataState)
    ) => {
      if (latestRequestIdRef.current === requestId) setState(update);
    };
    const snapshotShape = normalizeSnapshotShape(options?.snapshotShape);
    const cacheScopeKey = appendRequestedParameterIntentCacheIdentity(
      normalizeCacheScopeKey(options?.cacheScopeKey),
      options?.requestedParameters
    );
    const browserCacheEnabled = shouldUseQuickFillBrowserCache(cacheScopeKey);
    const dataScopeKey = buildDataScopeKey(options?.dataScope);
    const deferUnplacedFamilyCatalog = options?.deferUnplacedFamilyCatalog === true;
    const cacheKey = buildQuickFillDataCacheKey(cacheScopeKey, parameterNames, snapshotShape, dataScopeKey);
    let cachedSnapshot = quickFillResponseCache.get(cacheKey);
    let cacheSourceKey = cacheKey;
    if (!cachedSnapshot) {
      const compatibleMemoryKey = findCompatibleCacheKey(
        Array.from(quickFillResponseCache.keys()).map((candidateKey) => ({ cacheKey: candidateKey })),
        cacheScopeKey,
        parameterNames,
        snapshotShape,
        dataScopeKey
      );
      if (compatibleMemoryKey) {
        cachedSnapshot = quickFillResponseCache.get(compatibleMemoryKey);
        cacheSourceKey = compatibleMemoryKey;
        if (cachedSnapshot) {
          quickFillResponseCache.set(cacheKey, cachedSnapshot);
          perf.log('QF-Data', 'Compatible memory cache hit', {
            requestedCacheKey: cacheKey,
            cacheKey: compatibleMemoryKey,
            cacheScopeKey,
            snapshotShape,
            paramCount: parameterNames?.length ?? 0,
          });
        }
      }
    }
    if (!cachedSnapshot && browserCacheEnabled) {
      const tSessionHydrate = performance.now();
      cachedSnapshot = readQuickFillSessionSnapshot(cacheKey) ?? undefined;
      if (cachedSnapshot) {
        cacheSourceKey = cacheKey;
        quickFillResponseCache.set(cacheKey, cachedSnapshot);
        perf.log('QF-Data', 'Session cache hit', {
          cacheKey,
          cacheScopeKey,
          snapshotShape,
          families: cachedSnapshot.families.length,
          elements: cachedSnapshot.pipeElements.length,
          availableParameters: cachedSnapshot.availableParameters.length,
        });
        perf.slow('QF-Data', 'session cache hydration', tSessionHydrate, 25, {
          cacheKey,
          cacheScopeKey,
          snapshotShape,
          families: cachedSnapshot.families.length,
          elements: cachedSnapshot.pipeElements.length,
          availableParameters: cachedSnapshot.availableParameters.length,
        });
      }
    }
    if (!cachedSnapshot && browserCacheEnabled) {
      const tPersistentHydrate = performance.now();
      let persistentCacheKey = cacheKey;
      cachedSnapshot = await readQuickFillPersistentSnapshot(persistentCacheKey) ?? undefined;
      if (!cachedSnapshot) {
        const compatiblePersistentKey = findCompatibleCacheKey(
          readQuickFillPersistentMarkers(),
          cacheScopeKey,
          parameterNames,
          snapshotShape,
          dataScopeKey
        );
        if (compatiblePersistentKey && compatiblePersistentKey !== cacheKey) {
          persistentCacheKey = compatiblePersistentKey;
          cachedSnapshot = await readQuickFillPersistentSnapshot(compatiblePersistentKey) ?? undefined;
        }
      }
      if (cachedSnapshot) {
        cacheSourceKey = persistentCacheKey;
        quickFillResponseCache.set(cacheKey, cachedSnapshot);
        perf.log('QF-Data', 'Persistent cache hit', {
          cacheKey: persistentCacheKey,
          requestedCacheKey: cacheKey,
          cacheScopeKey,
          snapshotShape,
          families: cachedSnapshot.families.length,
          elements: cachedSnapshot.pipeElements.length,
          availableParameters: cachedSnapshot.availableParameters.length,
        });
        perf.slow('QF-Data', 'persistent cache lookup', tPersistentHydrate, 50, {
          cacheKey,
          cacheScopeKey,
          snapshotShape,
          families: cachedSnapshot.families.length,
          elements: cachedSnapshot.pipeElements.length,
          availableParameters: cachedSnapshot.availableParameters.length,
        });
      } else if (hasQuickFillPersistentMarker(cacheKey)) {
        perf.warn('QF-Data', 'Persistent cache marker exists but snapshot was not readable', {
          cacheKey,
          cacheScopeKey,
          snapshotShape,
        });
      }
    }
    if (cachedSnapshot) {
      cachedSnapshot = scopeQuickFillSnapshot(cachedSnapshot, options?.dataScope, parameterNames);
      quickFillResponseCache.set(cacheKey, cachedSnapshot);
      const tCacheHit = performance.now();
      perf.log('QF-Data', 'Cache hit', {
        cacheKey: cacheSourceKey,
        requestedCacheKey: cacheKey,
        cacheScopeKey,
        snapshotShape,
        paramCount: parameterNames?.length ?? 0,
      });
      const nextState = buildSuccessState(cachedSnapshot, snapshotShape, cacheScopeKey, dataScopeKey);
      perf.slow('QF-Data', 'cache hit state hydration', tCacheHit, 80, {
        families: cachedSnapshot.families.length,
        elements: cachedSnapshot.pipeElements.length,
        availableParameters: cachedSnapshot.availableParameters.length,
        snapshotShape,
      });
      const tCacheSetState = performance.now();
      setLatestState(nextState);
      perf.slow('QF-Data', 'cache hit setState dispatch', tCacheSetState, 40, {
        families: cachedSnapshot.families.length,
        elements: cachedSnapshot.pipeElements.length,
      });
      return {
        success: true,
        source: 'cache',
        familiesCount: cachedSnapshot.families.length,
        elementsCount: cachedSnapshot.pipeElements.length,
        availableParametersCount: cachedSnapshot.availableParameters.length,
        modelVersion: readCacheScopeModelVersion(cacheScopeKey),
        cacheScopeKey,
      };
    }

    const joinedRequest = quickFillInFlightRequests.get(cacheKey);
    if (joinedRequest) {
      perf.log('QF-Data', 'Joining in-flight request', {
        cacheKey,
        cacheScopeKey,
        snapshotShape,
        paramCount: parameterNames?.length ?? 0,
      });
      setLatestState(prev => buildLoadingState(prev, snapshotShape, cacheScopeKey, dataScopeKey));
      try {
        const snapshot = await joinedRequest;
        const tJoinedState = performance.now();
        setLatestState(buildSuccessState(snapshot, snapshotShape, cacheScopeKey, dataScopeKey));
        perf.slow('QF-Data', 'joined request state hydration', tJoinedState, 25, {
          families: snapshot.families.length,
          elements: snapshot.pipeElements.length,
          availableParameters: snapshot.availableParameters.length,
          snapshotShape,
        });
        return {
          success: true,
          source: 'joined',
          familiesCount: snapshot.families.length,
          elementsCount: snapshot.pipeElements.length,
          availableParametersCount: snapshot.availableParameters.length,
          modelVersion: readCacheScopeModelVersion(cacheScopeKey),
          cacheScopeKey,
        };
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to join QuickFill request';
        setLatestState(prev => ({ ...prev, ...buildErrorState(message) }));
        return buildFetchFailureResult('error');
      }
    }

    setLatestState(prev => buildLoadingState(prev, snapshotShape, cacheScopeKey, dataScopeKey));
    const t0 = performance.now();

    try {
      if (!isGrpcRuntimeAvailable()) {
        perf.log('QF-Data', 'gRPC not available, no data source');
        setLatestState(prev => ({ ...prev, isLoading: false, isLoaded: true }));
        return buildFetchFailureResult('unavailable');
      }

      perf.log('QF-Data', 'gRPC call START', {
        cacheScopeKey,
        snapshotShape,
        dataScopeKey,
        paramCount: parameterNames?.length ?? 0,
        sampleRequestedParams: parameterNames?.slice(0, 6) ?? [],
      });
      let authoritativeModelVersion: number | undefined;
      let authoritativeCacheScopeKey = cacheScopeKey;
      const requestPromise = (async () => {
        const tGrpc = performance.now();
        const endGrpc = perf.start('QF-Data', 'gRPC call');
        const result = await getQuickFillDataGrpc(parameterNames, {
          onProgress: options?.onProgress,
          snapshotShape,
          dataScope: options?.dataScope,
          deferUnplacedFamilyCatalog,
          requestedParameters: options?.requestedParameters,
        });
        authoritativeModelVersion = result.modelVersion;
        authoritativeCacheScopeKey = replaceCacheScopeModelVersion(
          cacheScopeKey,
          authoritativeModelVersion
        );
        endGrpc();
        perf.slow('QF-Data', 'gRPC call await', tGrpc, 100, {
          cacheScopeKey,
          snapshotShape,
          paramCount: parameterNames?.length ?? 0,
        });
        const tMap = performance.now();
        const endMap = perf.start('QF-Data', 'mapping');
        const snapshot = mapQuickFillGrpcResultToSnapshot(result, options?.dataScope, parameterNames);
        endMap();
        perf.slow('QF-Data', 'map gRPC snapshot', tMap, 50, {
          families: snapshot.families.length,
          elements: snapshot.pipeElements.length,
          availableParameters: snapshot.availableParameters.length,
          snapshotShape,
        });
        return snapshot;
      })();

      quickFillInFlightRequests.set(cacheKey, requestPromise);
      const snapshot = await requestPromise;
      const authoritativeCacheKey = buildQuickFillDataCacheKey(
        authoritativeCacheScopeKey,
        parameterNames,
        snapshotShape,
        dataScopeKey
      );
      const tCacheWrite = performance.now();
      quickFillResponseCache.set(authoritativeCacheKey, snapshot);
      if (shouldUseQuickFillBrowserCache(authoritativeCacheScopeKey)) {
        writeQuickFillSessionSnapshot(authoritativeCacheKey, snapshot);
        writeQuickFillPersistentSnapshot(authoritativeCacheKey, snapshot);
      } else {
        perf.log('QF-Data', 'Skipped browser cache write for unversioned Quick Fill scope', {
          cacheScopeKey,
          snapshotShape,
        });
      }
      perf.slow('QF-Data', 'clone snapshot into response cache', tCacheWrite, 50, {
        families: snapshot.families.length,
        elements: snapshot.pipeElements.length,
        availableParameters: snapshot.availableParameters.length,
        snapshotShape,
      });
      const tNetworkState = performance.now();
      setLatestState(buildSuccessState(snapshot, snapshotShape, authoritativeCacheScopeKey, dataScopeKey));
      perf.slow('QF-Data', 'network setState dispatch', tNetworkState, 10, {
        families: snapshot.families.length,
        elements: snapshot.pipeElements.length,
      });
      logQuickFillSnapshotSuccess(authoritativeCacheKey, snapshot);

      return {
        success: true,
        source: 'network',
        familiesCount: snapshot.families.length,
        elementsCount: snapshot.pipeElements.length,
        availableParametersCount: snapshot.availableParameters.length,
        modelVersion: authoritativeModelVersion,
        cacheScopeKey: authoritativeCacheScopeKey,
      };
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error loading QuickFill data';
      perf.warn('QF-Data', 'Error', { error: msg });
      setLatestState(prev => ({ ...prev, ...buildErrorState(msg) }));
      return buildFetchFailureResult('error');
    } finally {
      quickFillInFlightRequests.delete(cacheKey);
      perf.measure('QF-Data', 'total', t0);
    }
  }, []);

  return {
    families: state.families,
    pipeElements: state.pipeElements,
    availableParameters: state.availableParameters,
    isLoading: state.isLoading,
    isLoaded: state.isLoaded,
    error: state.error,
    cacheScopeKey: state.cacheScopeKey,
    fetchQuickFillData,
  };
}

export type { QuickFillPipeElement, QuickFillDataSnapshot };
export {
  getLastQuickFillDataCacheProbeDetails,
  hasQuickFillDataCache,
  __resetQuickFillDataCacheForTests,
  __resetQuickFillDataMemoryCacheForTests,
  scopeQuickFillSnapshot,
};
